import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { OfferService } from "@gen/tracker/v1/offer_connect";
import { keepPreviousData } from "@tanstack/react-query";
import { Offer, OfferQuickView, OfferStatus } from "@gen/tracker/v1/offer_pb";
import type { CheckOfferLinkResponse, GetOfferLibraryResponse, ListOfferNumbersResponse } from "@gen/tracker/v1/offer_pb";
import type { PayoutModel } from "@gen/tracker/v1/affiliate_network_pb";
import { DateRange, type FieldError, ListSort, Pagination } from "@gen/tracker/v1/common_pb";
import { fetchAll, useByIds } from "@/lib/services/catalog";
import { basisToProto } from "@/lib/services/analytics-service";
import type { ConversionBasis, DayRange } from "@/lib/period";

const client = createClient(OfferService);

export const offerKeys = {
  all: ["offers"] as const,
  lists: ["offers", "list"] as const,
  list: (params: ListOffersParams) => [...offerKeys.all, "list", params] as const,
  detail: (id: string) => [...offerKeys.all, "detail", id] as const,
};

export interface ListOffersParams {
  search?: string;
  networkId?: string;
  groupId?: string;
  statuses?: OfferStatus[];
  /** Any of these countries (ISO-3166 alpha-2) — the «Страна» chip. */
  countries?: string[];
  /** Any of these default payout models — the «Модель» chip. */
  payoutModels?: PayoutModel[];
  /** Authored by these people. */
  ownerIds?: string[];
  /** «Мои»: the caller's own, as the server knows him — never his sub named. */
  mine?: boolean;
  tagIds?: string[];
  /** Exactly these offers (useOffersByIds is the reader for names). */
  ids?: string[];
  /** One of GetOfferLibrary's attention tiles, resolved by the server — its total is the tile's count. */
  quickView?: OfferQuickView;
  /** name | status | created_at; none = newest first. */
  sort?: ListSort;
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
}

export function useListOffers(
  params: ListOffersParams = {},
  options?: Partial<UseQueryOptions<{ items: Offer[]; total: number }>>,
) {
  return useQuery({
    queryKey: offerKeys.list(params),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.listOffers({
        search: params.search ?? "",
        networkId: params.networkId ?? "",
        groupId: params.groupId ?? "",
        statuses: params.statuses ?? [],
        countries: params.countries ?? [],
        payoutModels: params.payoutModels ?? [],
        ownerIds: params.ownerIds ?? [],
        mine: params.mine ?? false,
        tagIds: params.tagIds ?? [],
        ids: params.ids ?? [],
        quickView: params.quickView ?? OfferQuickView.UNSPECIFIED,
        sort: params.sort,
        pagination: new Pagination({
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        }),
      });
      return {
        items: res.items,
        total: res.pagination?.total ?? res.items.length,
      };
    },
    ...options,
  });
}

/** Every offer under the filter, page after page — one partner's offers on its card. */
export function useAllOffers(params: Pick<ListOffersParams, "networkId" | "statuses">, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...offerKeys.all, "list", "all", params] as const,
    enabled: options?.enabled ?? true,
    queryFn: () =>
      fetchAll(async (offset, limit) => {
        const res = await client.listOffers({
          networkId: params.networkId ?? "",
          statuses: params.statuses ?? [],
          pagination: new Pagination({ limit, offset }),
        });
        return { items: res.items, total: res.pagination?.total ?? res.items.length };
      }),
  });
}

/** The offers with these ids, by id — however deep in the catalog they sit. */
export function useOffersByIds(ids: Iterable<string>, enabled = true) {
  return useByIds<Offer>(
    offerKeys.all,
    ids,
    async (chunk) => (await client.listOffers({ ids: chunk, pagination: new Pagination({ limit: chunk.length, offset: 0 }) })).items,
    enabled,
  );
}

export function useGetOffer(id: string | undefined) {
  return useQuery({
    queryKey: offerKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getOffer({ id });
      return res.offer;
    },
  });
}

export function useCreateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (offer: Offer) => {
      const res = await client.createOffer({ offer });
      return res.offer!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: offerKeys.all });
    },
  });
}

export function useUpdateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (offer: Offer) => {
      const res = await client.updateOffer({ offer });
      return res.offer!;
    },
    onSuccess: (offer) => {
      void qc.invalidateQueries({ queryKey: offerKeys.all });
      qc.setQueryData(offerKeys.detail(offer.id), offer);
    },
  });
}

export function useArchiveOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveOffer({ id });
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: offerKeys.all });
      // Lists only: a detail or a by-ids map under the same root has no
      // `items` to filter.
      const snapshots = qc.getQueriesData<{ items: Offer[]; total: number }>({
        queryKey: offerKeys.lists,
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          items: data.items.filter((o) => o.id !== id),
          total: Math.max(0, data.total - 1),
        });
      }
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: offerKeys.all });
    },
  });
}

// Usage --------------------------------------------------------------------

export interface OfferUsage {
  flowCount: number;
  campaignCount: number;
  flowIds: string[];
}

export const offerUsageKeys = {
  for: (id: string) => [...offerKeys.detail(id), "usage"] as const,
};

export function useOfferUsage(id: string | undefined) {
  return useQuery({
    queryKey: id ? offerUsageKeys.for(id) : ["offers", "usage-disabled"],
    enabled: !!id,
    staleTime: 60_000,
    queryFn: async (): Promise<OfferUsage> => {
      if (!id) return { flowCount: 0, campaignCount: 0, flowIds: [] };
      const res = await client.getOfferUsage({ id });
      const u = res.usage;
      return {
        flowCount: u?.flowCount ?? 0,
        campaignCount: u?.campaignCount ?? 0,
        flowIds: u?.flowIds ?? [],
      };
    },
  });
}

// Numbers --------------------------------------------------------------------

export interface OfferNumbersParams {
  ids: string[];
  range: DayRange;
  timezone: string;
  basis: ConversionBasis;
  compare?: DayRange;
}

/**
 * The offers on screen over the app's one period — the report engine's rows
 * (ListOfferNumbers), their campaigns and caps, and the engine's «Итого» of
 * exactly these offers.
 */
export function useOfferNumbers(p: OfferNumbersParams) {
  return useQuery({
    queryKey: [...offerKeys.all, "numbers", p] as const,
    enabled: p.ids.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ListOfferNumbersResponse> =>
      client.listOfferNumbers({
        ids: p.ids,
        dateRange: new DateRange({ from: p.range.from, to: p.range.to, timezone: p.timezone }),
        attributionBasis: basisToProto(p.basis),
        compareRange: p.compare ? new DateRange({ from: p.compare.from, to: p.compare.to, timezone: p.timezone }) : undefined,
      }),
  });
}

/**
 * The list's tiles (GetOfferLibrary): over every offer of the workspace, or
 * under the top bar's «Мои» over the caller's own only — the `mine` the list
 * itself asks with, so a tab's number is the table's total. The `mine` tile
 * is the caller's own as the server knows him.
 */
export function useOfferLibrary(mine: boolean) {
  return useQuery({
    queryKey: [...offerKeys.all, "library", mine] as const,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<GetOfferLibraryResponse> => client.getOfferLibrary({ mine }),
  });
}

/** Every save-time problem at once, each on its field (board OfferEditorErrors). */
export async function validateOffer(offer: Offer): Promise<FieldError[]> {
  return (await client.validateOffer({ offer })).errors;
}

/** «Проверить ссылку»: opens the link the way a click would; counts nothing. */
export function useCheckOfferLink() {
  return useMutation({
    mutationFn: async (url: string): Promise<CheckOfferLinkResponse> => client.checkOfferLink({ url }),
  });
}

/** A copy of the offer as a new draft: its own name, no id, no source. */
export function duplicateOffer(o: Offer, suffix: string): Offer {
  const copy = o.clone();
  copy.id = "";
  copy.source = "";
  copy.name = `${o.name} ${suffix}`.trim();
  copy.externalOfferId = "";
  copy.externalCampaignId = "";
  return copy;
}
